import { FC, ReactNode, useMemo } from "react";
import {
  axisClasses,
  BarPlot,
  BarPlotProps,
  ChartContainerPro,
  ChartContainerProProps,
  ChartsAxisHighlight,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  LinePlotProps,
  LineSeriesType,
  XAxis,
  YAxis,
} from "@mui/x-charts-premium";
import { TimeSeries } from "firefly-shared-ts-timeseries";
import { SxProps } from "@mui/material";
import { resolveXAxisConfig } from "./resolveXAxisConfig";
import { resolveYAxisConfig } from "./resolveYAxisConfig";
import { MakeOptional, useSxMergeStrict } from "shared-ts-mui";
import { resolveSeries } from "./resolveSeries";
import { resolveTimeAxisScaleType } from "./resolveTimeAxisScaleType";
import { resolveTimeAxisHighlightType } from "./resolveChartAxisHighlightType";
import { AllSeriesType } from "@mui/x-charts/models";

export type MuiTimeSeriesOptions = Omit<
  MakeOptional<LineSeriesType, "type">,
  "data"
>;

export interface TimeSeriesMuiChartProps {
  timeSeries: Array<TimeSeries<number | null>>;
  timeSeriesOptions?: Array<MuiTimeSeriesOptions>;
  yAxisConfig?: Array<YAxis<"linear">>;
  yAxisLabelOffset?: { x?: number };
  xAxisLabelOffset?: { y?: number };
  xAxisConfig?: XAxis<"time">;
  slotProps?: { BarPlotProps?: BarPlotProps; LinePlotProps?: LinePlotProps };
  width?: number;
  height?: number;
  margin?: Partial<ChartContainerProProps["margin"]> | number;
  sx?: SxProps;
  children?: ReactNode;
}

export const TimeSeriesMuiChart: FC<TimeSeriesMuiChartProps> = (props) => {
  const {
    timeSeries,
    timeSeriesOptions,
    xAxisConfig,
    yAxisConfig,
    width,
    height,
  } = props;

  const timeAxisScaleType = useMemo(
    () => resolveTimeAxisScaleType(timeSeriesOptions),
    [timeSeriesOptions]
  );
  const timeAxisHighlightType = useMemo(
    () => resolveTimeAxisHighlightType(timeAxisScaleType),
    [timeAxisScaleType]
  );

  const xAxis: Array<XAxis<"time">> = useMemo(() => {
    return resolveXAxisConfig(timeSeries, xAxisConfig, timeAxisScaleType);
  }, [timeSeries, xAxisConfig, timeAxisScaleType]);

  const yAxis: Array<YAxis<"linear">> = useMemo(() => {
    return resolveYAxisConfig(timeSeries, yAxisConfig);
  }, [timeSeries, yAxisConfig]);

  const series: Array<AllSeriesType> = useMemo(() => {
    return resolveSeries(timeSeries, timeSeriesOptions);
  }, [timeSeriesOptions, timeSeries]);

  const sx = useSxMergeStrict(() => [props.sx, {
    ...(props.yAxisLabelOffset?.x != null && {
      [`.${axisClasses.left} .${axisClasses.label}`]: {
        transform: `translateX(${props.yAxisLabelOffset.x}px)`,
      },
    }),
    ...(props.xAxisLabelOffset?.y != null && {
      [`.${axisClasses.bottom} .${axisClasses.label}`]: {
        transform: `translateY(${props.xAxisLabelOffset?.y}px)`,
      },
    }),
  }],[props.sx, props.xAxisLabelOffset?.y, props.yAxisLabelOffset?.x]);

  return (
    <ChartContainerPro
      margin={props.margin}
      xAxis={xAxis}
      yAxis={yAxis}
      series={series}
      width={width}
      height={height}
      sx={sx}
    >
      <ChartsXAxis />
      <ChartsYAxis />
      {props.children}
      <BarPlot {...props.slotProps?.BarPlotProps} />
      <LinePlot {...props.slotProps?.LinePlotProps} />
      <ChartsTooltip />
      <ChartsAxisHighlight x={timeAxisHighlightType} />
    </ChartContainerPro>
  );
};
